import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(sessionId ? 'checking' : 'failed');
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await fetch(`${BACKEND_URL}/api/checkout-status?session_id=${sessionId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (data.status === 'expired' || attempts.current >= 15) {
          setStatus('failed');
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 15) setStatus('failed');
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#050505' }} data-testid="checkout-success-page">
      <motion.div
        className="max-w-lg w-full text-center rounded-3xl border border-white/10 p-10 md:p-14"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(112,105,88,0.2) 0%, #0a0a0a 70%)' }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {status === 'checking' && (
          <div data-testid="pay-checking">
            <Loader2 size={44} className="mx-auto mb-6 animate-spin" style={{ color: '#e2c179' }} />
            <p className="text-white text-lg font-semibold" style={{ fontFamily: "'Syne', sans-serif" }}>{t('payChecking')}</p>
          </div>
        )}

        {status === 'paid' && (
          <div data-testid="pay-success">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-8 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #706958, #e2c179)', boxShadow: '0 0 60px rgba(226,193,121,0.4)' }}
            >
              <CheckCircle size={38} className="text-black" />
            </motion.div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Syne', sans-serif" }} data-testid="pay-success-title">
              {t('paySuccessTitle')}
            </h1>
            <p className="text-gray-400 text-sm leading-relaxed mb-10">{t('paySuccessText')}</p>
            <button
              className="btn-accent mx-auto"
              onClick={() => navigate('/')}
              data-testid="pay-success-home-btn"
            >
              CEXUS
              <ArrowRight size={15} />
            </button>
          </div>
        )}

        {status === 'failed' && (
          <div data-testid="pay-failed">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full flex items-center justify-center border border-white/15 bg-white/[0.05]">
              <AlertCircle size={36} className="text-gray-400" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Syne', sans-serif" }} data-testid="pay-failed-title">
              {t('payFailedTitle')}
            </h1>
            <p className="text-gray-400 text-sm leading-relaxed mb-10">{t('payFailedText')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <button className="btn-accent" onClick={() => navigate('/services/customize')} data-testid="pay-retry-btn">
                {t('payTryAgain')}
              </button>
              <button
                className="px-7 py-3.5 rounded-none text-sm font-medium border border-white/25 text-white transition-all hover:border-white/60"
                onClick={() => navigate('/services')}
                data-testid="pay-back-btn"
              >
                {t('payBackServices')}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
